import { FC, useState } from 'react'
import { Box, Button, Chip, CircularProgress, Collapse, Stack, Typography } from '@mui/material'
import HistoryIcon from '@mui/icons-material/History'
import { documentoService } from '@/services/documentoService'
import { ehDocumentoExpurgado } from '@/utils/documentoExpurgado'
import { Documento, DocumentoVersao } from '@/types/documento'
import { formatDate, formatDateTime } from '@/utils/formatters'

interface Props {
  /** O documento ATIVO (vigente) — é o que as listagens devolvem. */
  documento: Documento
}

/** Autor como o consultor pode vê-lo: "Você" ou "Originei" — nunca o nome de quem opera. */
const rotuloAutor = (v: DocumentoVersao): string =>
  v.autor === 'VOCE' ? 'Você' : v.autor === 'ORIGINEI' ? 'Originei' : v.autorId ? `usuário ${v.autorId.slice(0, 8)}` : '—'

/** L17: versão expurgada não tem nome nem arquivo — só a data do expurgo (`expurgadoEm` é o sinal). */
const ehExpurgada = (v: DocumentoVersao): boolean => !!v.expurgadoEm

/**
 * Versões do documento (Onda 4, L11 — Q37/Q38): marca o arquivo como **Vigente** e, quando houve
 * troca, oferece **"Versões anteriores (n)"** — a trilha vem de `GET /documentos/{id}/versoes`
 * só quando aberta (o `versao` da listagem já diz quantas são, sem chamada por linha). Cada
 * versão anterior mostra nome, data de envio, autor ("Você"/"Originei"), quando foi substituída
 * e o motivo da troca. Só metadados: o arquivo de versão anterior não é baixável pelo consultor
 * (a versão trocada está invalidada).
 */
export const VersoesDocumento: FC<Props> = ({ documento }) => {
  const versao = documento.versao ?? 1
  const anteriores = Math.max(0, versao - 1)
  const [aberto, setAberto] = useState(false)
  const [estado, setEstado] = useState<'ocioso' | 'carregando' | 'ok' | 'erro' | 'expurgado'>('ocioso')
  const [versoes, setVersoes] = useState<DocumentoVersao[]>([])

  const alternar = async () => {
    const abrir = !aberto
    setAberto(abrir)
    if (!abrir || estado === 'ok' || estado === 'carregando') return
    setEstado('carregando')
    try {
      const lista = await documentoService.versoes(documento.id)
      setVersoes(lista ?? [])
      setEstado('ok')
    } catch (e) {
      // L17 (AN-12 §5.4): a cadeia foi expurgada — 410 DOCUMENTO_EXPURGADO, não é falha.
      setEstado(ehDocumentoExpurgado(e) ? 'expurgado' : 'erro')
    }
  }

  const listaAnteriores = versoes.filter((v) => !v.vigente).sort((a, b) => b.numero - a.numero)
  const motivoDaVigente = versoes.find((v) => v.vigente)?.motivo

  return (
    <Box component="span" sx={{ display: 'block' }}>
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap component="span">
        <Chip
          size="small"
          color="success"
          variant="outlined"
          label={versao > 1 ? `Vigente · versão ${versao}` : 'Vigente'}
        />
        {anteriores > 0 && (
          <Button
            size="small"
            variant="text"
            startIcon={<HistoryIcon fontSize="small" />}
            onClick={() => void alternar()}
            aria-expanded={aberto}
            aria-label={`Versões anteriores (${anteriores}) de ${documento.nome}`}
            sx={{ textTransform: 'none', py: 0 }}
          >
            Versões anteriores ({anteriores})
          </Button>
        )}
      </Stack>
      {anteriores > 0 && (
        <Collapse in={aberto} unmountOnExit>
          <Box
            role="list"
            aria-label={`Versões anteriores de ${documento.nome}`}
            sx={{ mt: 0.5, pl: 1.5, borderLeft: '2px solid', borderColor: 'divider' }}
          >
            {estado === 'carregando' && <CircularProgress size={14} aria-label="Carregando versões" />}
            {estado === 'erro' && (
              <Typography variant="caption" color="error" component="div">
                Não foi possível carregar as versões anteriores.
              </Typography>
            )}
            {estado === 'expurgado' && (
              <Typography variant="caption" color="text.secondary" component="div" data-testid="versoes-expurgadas">
                Arquivo expurgado — as versões anteriores não estão mais disponíveis.
              </Typography>
            )}
            {estado === 'ok' && motivoDaVigente && (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                Motivo da troca para a versão vigente: {motivoDaVigente}
              </Typography>
            )}
            {estado === 'ok' &&
              listaAnteriores.map((v) => (
                <Box key={v.id} role="listitem" sx={{ py: 0.25 }}>
                  <Typography variant="caption" component="div" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
                    {ehExpurgada(v)
                      ? `Versão ${v.numero} · Arquivo expurgado${v.expurgadoEm ? ` em ${formatDate(v.expurgadoEm)}` : ''}`
                      : `Versão ${v.numero} · ${v.nome}`}
                    {v.emRetencaoLegal && (
                      <Chip
                        component="span"
                        size="small"
                        color="error"
                        variant="outlined"
                        label="Em retenção legal"
                        sx={{ ml: 0.5, height: 18 }}
                      />
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    Enviada em {formatDateTime(v.enviadoEm)} por {rotuloAutor(v)}
                    {v.substituidaEm ? ` · substituída em ${formatDateTime(v.substituidaEm)}` : ''}
                    {v.removida ? ' · removida' : ''}
                  </Typography>
                  {v.motivo && (
                    <Typography variant="caption" color="text.secondary" component="div">
                      Motivo do envio desta versão: {v.motivo}
                    </Typography>
                  )}
                  {v.removida && v.motivoRemocao && (
                    <Typography variant="caption" color="text.secondary" component="div">
                      Motivo da remoção: {v.motivoRemocao}
                    </Typography>
                  )}
                </Box>
              ))}
          </Box>
        </Collapse>
      )}
    </Box>
  )
}

export default VersoesDocumento
